import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

type Props = {
  open: boolean;
  onClose: () => void;
};

const DURATION = 0.3; // seconds

export default function Hamburger({ open, onClose }: Props) {
  const navigate = useNavigate();
  const [onApply, setOnApply] = useState(false);
  const [onIntroduction, setOnIntroduction] = useState(false);
  const [onIntroductionFunc, setOnIntroductionFunc] = useState(false);
  //  열렸을 때 body scroll lock
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  //  닫힐 때 아코디언 상태 초기화
  useEffect(() => {
    if (open) return;
    setOnApply(false);
    setOnIntroduction(false);
    setOnIntroductionFunc(false);
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[1001]"
          // wrapper는 즉시 표시해도 되지만, 혹시 모를 깜빡임 방지로 fade 살짝
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 1 }}
        >
          {/* dim */}
          <motion.button
            type="button"
            aria-label="메뉴 닫기"
            onClick={onClose}
            className="absolute inset-0 bg-black/45"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: DURATION } }}
            exit={{ opacity: 0, transition: { duration: DURATION } }}
          />

          {/*  위에서 아래로 내려오는 패널 */}
          <motion.aside
            className={[
              "overflow-y-auto absolute left-0 top-0 w-full bg-white shadow-xl",
              "h-full", // 전체 덮기
            ].join(" ")}
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ duration: DURATION, ease: "easeOut" }}
          >
            <div className="h-20 px-4 flex items-center justify-between border-b border-b-[#DDE0E6]">
              <img
                onClick={() => {
                  onClose();
                  navigate("/");
                }}
                src="/assets/images/logo/logo_black.svg"
                alt="Poom"
                className="x-[78px] h-[26px] cursor-pointer"
              />
              <button
                type="button"
                onClick={onClose}
                className="w-12 h-12 grid place-items-center"
                aria-label="닫기"
              >
                <img src="/assets/images/icon/close.svg" alt="close" width={24} height={24} />
              </button>
            </div>

            <nav>
              {/* <Link
                to="/features"
                onClick={onClose}
                className="font-bold text-base flex py-4 px-4 border-b-[1px] border-b-[#DDE0E6]"
              >
                기능소개
              </Link> */}
              <div
                className="flex flex-row w-full justify-between items-center border-b-[1px] border-b-[#DDE0E6] cursor-pointer px-4 h-[74px]"
                onClick={() => {
                  setOnIntroductionFunc((v) => {
                    const next = !v;
                    if (next) {
                      setOnApply(false);
                      setOnIntroduction(false);
                    }
                    return next;
                  });
                }}
                role="button"
                aria-expanded={onIntroductionFunc}
              >
                <div
                  className={[
                    "font-bold text-base",
                    onIntroductionFunc ? "text-[#0079FF]" : "text-[#121319]",
                  ].join(" ")}
                >
                  기능소개
                </div>

                <img
                  src="/assets/images/icon/arrow_down.png"
                  alt="arrow"
                  className={[
                    "w-6 h-6 transition-transform duration-200",
                    onIntroductionFunc ? "rotate-180" : "rotate-0",
                  ].join(" ")}
                />
              </div>

              <div
                className={[
                  "overflow-hidden transition-[max-height,opacity] duration-300 ease-out",
                  onIntroductionFunc ? "max-h-60 opacity-100" : "max-h-0 opacity-0",
                ].join(" ")}
              >
                <Link
                  to="/features#settlement-history"
                  onClick={onClose}
                  className="flex items-center gap-2 px-4 py-3 border-b-[1px] border-b-[#DDE0E6] text-[16px] text-[#121319]"
                >
                  <img
                    src="/assets/images/icon/header_list.svg"
                    alt="introduction"
                    className="w-6 h-6"
                  />
                  결제/정산 내역
                </Link>

                <Link
                  to="/features#payout-service"
                  onClick={onClose}
                  className="flex items-center gap-2 px-4 py-3 border-b-[1px] border-b-[#DDE0E6] text-[16px] text-[#121319]"
                >
                  <img
                    src="/assets/images/icon/header_settle.svg"
                    alt="effect"
                    className="w-6 h-6"
                  />
                  정산 서비스
                </Link>

                <Link
                  to="/features#merchant-mgmt"
                  onClick={onClose}
                  className="flex items-center gap-2 px-4 py-3 border-b-[1px] border-b-[#DDE0E6] text-[16px] text-[#121319]"
                >
                  <img
                    src="/assets/images/icon/header_merchant.svg"
                    alt="cases"
                    className="w-6 h-6"
                  />
                  가맹점 서비스
                </Link>
                <Link
                  to="/features#payment-system"
                  onClick={onClose}
                  className="flex items-center gap-2 px-4 py-3 border-b-[1px] border-b-[#DDE0E6] text-[16px] text-[#121319]"
                >
                  <img
                    src="/assets/images/icon/header_payment.svg"
                    alt="cases"
                    className="w-6 h-6"
                  />
                  결제 서비스
                </Link>
                <Link
                  to="/features#sales-network"
                  onClick={onClose}
                  className="flex items-center gap-2 px-4 py-3 border-b-[1px] border-b-[#DDE0E6] text-[16px] text-[#121319]"
                >
                  <img src="/assets/images/icon/header_sale.svg" alt="cases" className="w-6 h-6" />
                  영업대행 관리 서비스
                </Link>
              </div>
              {/* 서비스 신청하기 (아코디언) */}
              <div
                className="flex flex-row w-full justify-between items-center border-b-[1px] border-b-[#DDE0E6] cursor-pointer px-4 h-[74px]"
                onClick={() => {
                  setOnApply((v) => {
                    const next = !v;
                    if (next) {
                      setOnIntroduction(false);
                      setOnIntroductionFunc(false);
                    }
                    return next;
                  });
                }}
                role="button"
                aria-expanded={onApply}
              >
                <div
                  className={[
                    "font-bold text-base",
                    onApply ? "text-[#0079FF]" : "text-[#121319]",
                  ].join(" ")}
                >
                  서비스 안내
                </div>

                <img
                  src="/assets/images/icon/arrow_down.png"
                  alt="arrow"
                  className={[
                    "w-6 h-6 transition-transform duration-200",
                    onApply ? "rotate-180" : "rotate-0",
                  ].join(" ")}
                />
              </div>

              <div
                className={[
                  "overflow-hidden transition-[max-height,opacity] duration-300 ease-out",
                  onApply ? "max-h-60 opacity-100" : "max-h-0 opacity-0",
                ].join(" ")}
              >
                <Link
                  to="/solution"
                  onClick={onClose}
                  className="flex items-center gap-2 px-4 py-3 border-b-[1px] border-b-[#DDE0E6] text-[16px] text-[#121319]"
                >
                  <img
                    src="/assets/images/icon/header_intro.svg"
                    alt="introduction"
                    className="w-6 h-6"
                  />
                  품 소개
                </Link>

                <Link
                  to="/efficiency"
                  onClick={onClose}
                  className="flex items-center gap-2 px-4 py-3 border-b-[1px] border-b-[#DDE0E6] text-[16px] text-[#121319]"
                >
                  <img
                    src="/assets/images/icon/header_effect.svg"
                    alt="effect"
                    className="w-6 h-6"
                  />
                  도입효과
                </Link>

                <Link
                  to="/case-studies"
                  onClick={onClose}
                  className="flex items-center gap-2 px-4 py-3 border-b-[1px] border-b-[#DDE0E6] text-[16px] text-[#121319]"
                >
                  <img src="/assets/images/icon/header_case.svg" alt="cases" className="w-6 h-6" />
                  협력사와 사례
                </Link>
              </div>

              {/* 회사소개 (아코디언) */}
              <div
                className="flex flex-row w-full justify-between items-center border-b-[1px] border-b-[#DDE0E6] cursor-pointer px-4 h-[74px]"
                onClick={() => {
                  setOnIntroduction((v) => {
                    const next = !v;
                    if (next) {
                      setOnApply(false);
                      setOnIntroductionFunc(false);
                    }
                    return next;
                  });
                }}
                role="button"
                aria-expanded={onIntroduction}
              >
                <div
                  className={[
                    "font-bold text-base",
                    onIntroduction ? "text-[#0079FF]" : "text-[#121319]",
                  ].join(" ")}
                >
                  서비스 신청하기
                </div>

                <img
                  src="/assets/images/icon/arrow_down.png"
                  alt="arrow"
                  className={[
                    "w-6 h-6 transition-transform duration-200",
                    onIntroduction ? "rotate-180" : "rotate-0",
                  ].join(" ")}
                />
              </div>

              <div
                className={[
                  "overflow-hidden transition-[max-height,opacity] duration-300 ease-out",
                  onIntroduction ? "max-h-60 opacity-100" : "max-h-0 opacity-0",
                ].join(" ")}
              >
                <Link
                  to="/inquiry-form?type=adoption"
                  onClick={onClose}
                  className="flex items-center gap-2 px-4 py-3 border-b-[1px] border-b-[#DDE0E6] text-[16px] text-[#121319]"
                >
                  <img
                    src="/assets/images/icon/header_application.svg"
                    alt="adoption"
                    className="w-6 h-6"
                  />
                  품 도입문의
                </Link>
                <Link
                  to="/faq"
                  onClick={onClose}
                  className="flex items-center gap-2 px-4 py-3 border-b-[1px] border-b-[#DDE0E6] text-[16px] text-[#121319]"
                >
                  <img src="/assets/images/icon/header_faq.svg" alt="faq" className="w-6 h-6" />
                  FAQ
                </Link>

                <Link
                  to="/help-center"
                  onClick={onClose}
                  className="flex items-center gap-2 px-4 py-3 border-b-[1px] border-b-[#DDE0E6] text-[16px] text-[#121319]"
                >
                  <img
                    src="/assets/images/icon/header_inquiry.svg"
                    alt="inquiry"
                    className="w-6 h-6"
                  />
                  고객센터
                </Link>
              </div>

              <div
                onClick={() => {
                  window.open("https://www.wiezon.com/");
                }}
                className="
                  flex flex-row w-full gap-1 items-center border-b-[1px] border-b-[#DDE0E6] cursor-pointer px-4 h-[74px]
                "
              >
                <span className="font-bold text-base">회사소개</span>
                <img className="w-6 h-6" src="/assets/images/icon/introductionCor_black.svg" />
              </div>
            </nav>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
